import React, { useCallback } from "react";
import { Box, Button, Typography, CircularProgress } from "@mui/material";
import { useForm, FormProvider } from "react-hook-form";
import FormInput from "@/components/FormInput";
import FormNumberInput from "@/components/FormNumberInput";
import AccountNumberInput from "@/components/FormInput/AccountNumberInput";
import AsyncSelectWithSearch from "@/components/AsyncSelectWithSearch";
import { getBatteryOemsReq } from "@/api/userApi";
import { getBatteryListReq } from "@/api/batteryApi";
import { SizingToolFormProps, SizingFormData } from "../types";
import { FormValidationValue } from "@/types";
import {
  calculateTotalNominalPower,
  calculateTotalUsableCapacity,
} from "@/utils/calculateTotalNominalPower";

const SectionHeader: React.FC<{ title: string }> = ({ title }) => (
  <>
    <Typography
      sx={{
        fontWeight: 600,
        fontSize: { xs: 15, sm: 16 },
        mt: 1,
        mb: 0.5,
      }}
    >
      {title}
    </Typography>
    <Box
      sx={{
        height: "1px",
        background: "#E0E0E0",
        mb: 2,
      }}
    />
  </>
);

const CalculatedField: React.FC<{
  label: string;
  value: string | number;
  description: string;
  control: any;
}> = ({ label, value, description, control }) => (
  <Box sx={{ mb: 1 }}>
    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
      <Typography sx={{ fontSize: "12px", fontWeight: 500, color: "#373533" }}>
        {label}
      </Typography>
    </Box>
    <FormInput
      name={label.toLowerCase().replace(/\s+/g, "")}
      control={control}
      label=""
      value={value}
      disabled={true}
    />
    <Typography
      sx={{
        fontSize: "11px",
        color: "#666",
        marginTop: "4px",
      }}
    >
      {description}
    </Typography>
  </Box>
);

const InfoBox: React.FC = () => (
  <Box
    sx={{
      background: "#E3F2FD",
      border: "1px solid #2196F3",
      borderRadius: "8px",
      p: 1,
      mb: 2,
      fontSize: "14px",
      color: "#1976D2",
      lineHeight: 1.4,
      display: "flex",
      alignItems: "flex-start",
      gap: 1,
    }}
  >
    <Box
      sx={{
        width: 20,
        height: 20,
        borderRadius: "50%",
        background: "#2196F3",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        mt: 0.25,
      }}
    >
      <Typography
        sx={{
          color: "#fff",
          fontSize: "12px",
          fontWeight: "bold",
          lineHeight: 1,
        }}
      >
        !
      </Typography>
    </Box>
    <Typography sx={{ fontSize: "12px", color: "#1976D2" }}>
      If any data requires correction, please return to the Project Enrollment
      page to make the necessary changes. Ensure that all information is
      accurate before continuing with the system sizing process.
    </Typography>
  </Box>
);

const ActionButtons: React.FC<{
  onCancel: () => void;
  analyzing: boolean;
}> = ({ onCancel, analyzing }) => (
  <Box
    sx={{
      borderTop: "1px solid #EDEDED",
    }}
  >
    <Box
      sx={{
        display: "flex",
        gap: 2,
        pt: 2,
        pb: 1,
        width: "100%",
      }}
    >
      <Button
        variant="outlined"
        onClick={onCancel}
        sx={{
          flex: 1,
          height: 48,
          fontWeight: 600,
          fontSize: 16,
          border: "1px solid #0076CE",
          color: "#0076CE",
          background: "#fff",
          borderRadius: 0,
          textTransform: "none",
          "&:hover": {
            border: "1px solid #0076CE",
            background: "#f8f9ff",
          },
        }}
      >
        Cancel
      </Button>
      <Button
        variant="contained"
        type="submit"
        sx={{
          flex: 1,
          height: 48,
          fontWeight: 600,
          fontSize: 16,
          background: "#0076CE",
          color: "#fff",
          borderRadius: 0,
          textTransform: "none",
          boxShadow: "none",
          "&:hover": {
            background: "#0056a3",
            boxShadow: "none",
          },
        }}
        disabled={analyzing}
      >
        {analyzing ? <CircularProgress size={24} /> : "Analyze"}
      </Button>
    </Box>
  </Box>
);

export const SizingToolForm: React.FC<SizingToolFormProps> = ({
  sizingForm,
  setSizingForm,
  handleAnalyze,
  analyzing,
  setOpenSizingTool,
  useForm: useFormHook,
  getBatteryOems = getBatteryOemsReq,
  projectData,
}) => {
  const formInstance =
    typeof useFormHook === "function"
      ? useFormHook({
          defaultValues: sizingForm,
          mode: "onChange",
        })
      : useFormHook;

  const batteryManufacturer = formInstance.watch("batteryManufacturer");
  const batteryManufacturerId = formInstance.watch("batteryManufacturerId");
  const batteryProductName = formInstance.watch("batteryProductName");
  const batteryQuantity = formInstance.watch("batteryQuantity");
  const individualBatteryCapacity = formInstance.watch("usableCapacity");
  const nominalPower = formInstance.watch("nominalPower");

  const fieldsDisabled = !!projectData;

  const fetchBatteryData = useCallback(
    async (selectedBatteryName: string) => {
      if (fieldsDisabled || !batteryManufacturerId) return;

      try {
        const response = await getBatteryListReq(
          String(batteryManufacturerId),
          selectedBatteryName
        );

        const batteryModels = response?.data?.batteryModels || [];
        const selectedBattery = batteryModels.find(
          (battery: any) => battery.name === selectedBatteryName
        );

        if (selectedBattery) {
          const updates: Partial<typeof sizingForm> = {};

          if (selectedBattery.nominalPowerKw) {
            updates.nominalPower = selectedBattery.nominalPowerKw;
            formInstance.setValue(
              "nominalPower",
              selectedBattery.nominalPowerKw
            );
          }

          const capacityValue =
            selectedBattery.totalCapacityKwh ||
            selectedBattery.capacity ||
            selectedBattery.usableCapacity;

          if (capacityValue) {
            updates.usableCapacity = capacityValue;
            formInstance.setValue("usableCapacity", capacityValue);
          }

          if (Object.keys(updates).length > 0) {
            setSizingForm({ ...sizingForm, ...updates });
          }
        }
      } catch (error) {
        console.error("Failed to fetch battery data:", error);
      }
    },
    [
      fieldsDisabled,
      batteryManufacturerId,
      formInstance,
      setSizingForm,
      sizingForm,
    ]
  );

  const getBatteryListWithId = useCallback(
    async (searchValue: string) => {
      try {
        if (!batteryManufacturerId) {
          return { data: { batteryModels: [] } };
        }
        return await getBatteryListReq(
          String(batteryManufacturerId),
          searchValue
        );
      } catch (error) {
        return { data: { batteryModels: [] } };
      }
    },
    [batteryManufacturerId]
  );

  const handleBatteryManufacturerChange = () => {
    const formValues = formInstance.getValues();
    setSizingForm({
      ...sizingForm,
      batteryManufacturer: formValues.batteryManufacturer,
      batteryManufacturerId: formValues.batteryManufacturerId,
      batteryProductName: "",
      batteryQuantity: "",
    });
  };

  const handleBatteryProductChange = () => {
    const formValues = formInstance.getValues();
    setSizingForm({
      ...sizingForm,
      batteryQuantity: "",
    });

    if (formValues.batteryProductName && !fieldsDisabled) {
      fetchBatteryData(formValues.batteryProductName);
    }
  };

  const handleCustomerDataPopulated = (customerData: any) => {
    setSizingForm({
      ...sizingForm,
      zipCode: customerData.zipCode || sizingForm.zipCode,
      customerName: customerData.customerName || sizingForm.customerName,
    });
  };

  const handleFormSubmit = formInstance.handleSubmit(
    (formValues: SizingFormData) => {
      handleAnalyze(formValues);
    }
  );

  const totalBatteryUsableCapacity = calculateTotalUsableCapacity(
    individualBatteryCapacity,
    batteryQuantity
  );

  const totalNominalPower = calculateTotalNominalPower(
    nominalPower,
    batteryQuantity
  );

  return (
    <FormProvider {...formInstance}>
      <Box component="form" sx={{ width: "100%" }} onSubmit={handleFormSubmit}>
        <Box sx={{ py: 0 }}>
          <SectionHeader title="Customer Information" />

          <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
            <Box sx={{ flex: 1 }}>
              <AccountNumberInput
                name="accountNumber"
                label="Account Number"
                placeholder="123456789012"
                disabled={fieldsDisabled}
                onCustomerDataPopulated={handleCustomerDataPopulated}
                hideCustomerDataDisplay={true}
                skipDuplicateCheck={!!projectData}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <FormInput
                name="zipCode"
                control={formInstance.control}
                label="Zip Code"
                placeholder="92101"
                disabled={fieldsDisabled}
                controlProps={{
                  rules: {
                    required: "Zip code is required",
                    pattern: {
                      value: /^\d{5}$/,
                      message: "Zip code must be exactly 5 digits",
                    },
                  },
                }}
                inputProps={{
                  maxLength: 5,
                }}
              />
            </Box>
          </Box>

          <SectionHeader title="System Specifications" />

          <Box sx={{ display: "flex", gap: 1.5, mb: 1 }}>
            <Box sx={{ flex: 1 }}>
              <FormNumberInput
                name="existingSolarKwac"
                control={formInstance.control}
                label="Existing Solar System Size (KWAC)"
                controlProps={{ rules: { required: true } }}
                placeholder="6.5 (Enter 0 if none)"
                disabled={fieldsDisabled}
                autoFormat={true}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <FormNumberInput
                name="newSolarKwac"
                control={formInstance.control}
                label="New Solar System Size (KWAC)"
                controlProps={{ rules: { required: true } }}
                placeholder="8.5 (Enter 0 if none)"
                disabled={fieldsDisabled}
                autoFormat={true}
              />
            </Box>
          </Box>

          <Box sx={{ mb: 1 }}>
            <AsyncSelectWithSearch
              name="batteryManufacturer"
              idKey="batteryManufacturerId"
              control={formInstance.control}
              label="Battery Manufacturer"
              getItems={getBatteryOems}
              itemsKey="oems"
              placeholder="Select battery manufacturer"
              disabled={fieldsDisabled}
              controlProps={{ rules: { required: true } }}
              onValueChange={handleBatteryManufacturerChange}
            />
          </Box>

          <Box sx={{ mb: 1 }}>
            <AsyncSelectWithSearch
              name="batteryProductName"
              idKey="batteryProductId"
              control={formInstance.control}
              label="Battery Product Name"
              getItems={getBatteryListWithId}
              itemsKey="batteryModels"
              placeholder="Select battery product"
              disabled={fieldsDisabled}
              controlProps={{ rules: { required: true } }}
              onValueChange={handleBatteryProductChange}
            />
          </Box>

          <Box sx={{ mb: 1 }}>
            <FormInput
              name="batteryQuantity"
              control={formInstance.control}
              label="Battery Quantity"
              type="number"
              controlProps={{
                rules: {
                  required: ((value: FormValidationValue) => {
                    if (!value && value !== 0) {
                      return "Battery quantity is required";
                    }

                    const num = Number(value);

                    if (
                      Number.isNaN(num) ||
                      num <= 0 ||
                      !Number.isFinite(num) ||
                      !Number.isInteger(num)
                    ) {
                      return "Battery quantity is required";
                    }

                    return true;
                  }) as any,
                  validate: {
                    positiveNumber: (value: FormValidationValue) => {
                      const num = Number(value);
                      if (Number.isNaN(num) || num <= 0) {
                        return "Battery quantity must be a positive number";
                      }
                      if (!Number.isInteger(num)) {
                        return "Battery quantity must be a whole number";
                      }
                      return true;
                    },
                  },
                },
              }}
              placeholder="2"
              disabled={fieldsDisabled}
              inputProps={{ min: 1, step: 1 }}
            />
          </Box>

          <CalculatedField
            label="Total Usable Capacity (kWh)"
            value={totalBatteryUsableCapacity}
            description="Total usable capacity in kWh. Calculated as the number of batteries multiplied by each battery's capacity."
            control={formInstance.control}
          />

          <CalculatedField
            label="Total Nominal Power (kW)"
            value={totalNominalPower}
            description="Total nominal power in kW. Calculated as the number of batteries
              multiplied by each battery's nominal power."
            control={formInstance.control}
          />

          <InfoBox />
        </Box>

        <ActionButtons
          onCancel={() => setOpenSizingTool(false)}
          analyzing={analyzing}
        />
      </Box>
    </FormProvider>
  );
};

export default SizingToolForm;
